'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { HStack, VStack, Box } from '@terra/ui-styled-system';
import { Button } from '../../../../../components/styled/button';
import { Text } from '../../../../../components/styled/text';
import { Textarea } from '../../../../../components/styled/textarea';
import { LegacyDialog } from '../../../../../components/dialog';
import { IconButton } from '../../../../../components/icon-button';
import { Field } from '../../../../../components/field';
import { Badge } from '../../../../../components/badge';
import { Tabs } from '../../../../../components/tabs';
import {
  Alert01Icon,
  CheckmarkCircle02Icon,
  Copy01Icon,
  PencilEdit02Icon,
  ViewIcon,
  File01Icon,
  Maximize02Icon,
  Undo02Icon,
  FloppyDiskIcon,
  Cancel01Icon,
  FlashIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { MarkdownPreview } from '../../../../markdown-preview';
import { Tooltip } from '../../../../../components/tooltip';
import { SignalSelector } from './SignalSelector';
import type { AttackerToolInput } from './types';

type AttackerEditModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (editedData: AttackerToolInput) => void;
  data: AttackerToolInput;
  actionName?: string;
  renderMode?: 'dialog' | 'inline';
};
type PreviewMode = 'split' | 'edit' | 'preview';
type FieldValidation = {
  isValid: boolean;
  message?: string;
};

export function AttackerEditModal({
  isOpen,
  onClose,
  onSubmit,
  data,
  actionName: _actionName,
  renderMode = 'dialog',
}: AttackerEditModalProps) {
  const t = useTranslations('designSystem.chat.editAttackerModal');
  const [editedData, setEditedData] = useState<AttackerToolInput>(data);
  const [originalData, setOriginalData] = useState<AttackerToolInput>(data);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('split');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const dataCopy = { ...data };

      setEditedData(dataCopy);
      setOriginalData(dataCopy);
      setHasUnsavedChanges(false);
    }
  }, [isOpen, data]);
  const validateField = useCallback(
    (field: keyof AttackerToolInput, value?: string): FieldValidation => {
      if (field === 'signalId' && (!value || value.trim().length === 0)) {
        return { isValid: false, message: t('validation.signalIdRequired') };
      }

      if (field === 'userAttackPrompt' && value && value.trim().length < 10) {
        return {
          isValid: false,
          message: t('validation.userAttackPromptMinLength'),
        };
      }

      return { isValid: true };
    },
    [t],
  );
  const isDataValid = useMemo(() => {
    const signalIdValid = validateField(
      'signalId',
      editedData.signalId,
    ).isValid;
    const userAttackPromptValid = validateField(
      'userAttackPrompt',
      editedData.userAttackPrompt,
    ).isValid;

    return signalIdValid && userAttackPromptValid;
  }, [editedData, validateField]);
  const isFieldModified = useCallback(
    (field: keyof AttackerToolInput): boolean => {
      return originalData[field] !== editedData[field];
    },
    [originalData, editedData],
  );
  const handleFieldChange = useCallback(
    (field: keyof AttackerToolInput, value: string) => {
      setEditedData((prev) => ({
        ...prev,
        [field]: value || undefined,
      }));
      setHasUnsavedChanges(true);
    },
    [],
  );
  const handleResetField = useCallback(
    (field: keyof AttackerToolInput) => {
      handleFieldChange(field, originalData[field] || '');
    },
    [originalData, handleFieldChange],
  );
  const handleResetAll = useCallback(() => {
    setEditedData({ ...originalData });
    setHasUnsavedChanges(false);
  }, [originalData]);
  const handleCopyField = useCallback((value: string) => {
    navigator.clipboard.writeText(value);
  }, []);
  const handleSignalSelect = useCallback((signalId: string) => {
    setEditedData((prev) => ({
      ...prev,
      signalId: signalId,
    }));
    setHasUnsavedChanges(true);
  }, []);
  const handleSubmit = useCallback(() => {
    onSubmit(editedData);
    onClose();
  }, [editedData, onSubmit, onClose]);
  const handleClose = useCallback(() => {
    setEditedData(data);
    setHasUnsavedChanges(false);
    onClose();
  }, [data, onClose]);
  const modifiedFieldsCount = Object.keys(editedData).filter((key) =>
    isFieldModified(key as keyof AttackerToolInput),
  ).length;
  const renderLargeField = useCallback(
    ({
      field,
      label,
      icon,
      placeholder,
      required = false,
    }: {
      field: keyof AttackerToolInput;
      label: string;
      icon: React.ReactNode;
      placeholder: string;
      required?: boolean;
    }) => {
      const value = editedData[field] || '';
      const validation = required
        ? validateField(field, value)
        : { isValid: true };
      const isModified = isFieldModified(field);

      return (
        <Field.Root>
          <HStack justifyContent="space-between" alignItems="center" mb={1}>
            <HStack gap={2} alignItems="center">
              {icon}
              <Field.Label fontSize="xs" fontWeight="medium">
                {label}
                {required && (
                  <Text as="span" color="fg.error">
                    *
                  </Text>
                )}
              </Field.Label>
              {isModified && (
                <Badge variant="emphasis" size="sm">
                  {t('modified')}
                </Badge>
              )}
              {!validation.isValid && (
                <Tooltip title={validation.message}>
                  <Alert01Icon size={14} color="var(--colors-fg-error)" />
                </Tooltip>
              )}
            </HStack>
            <HStack gap={1}>
              {value && (
                <Tooltip title={t('copy')}>
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={() => handleCopyField(value)}
                    aria-label={t('copyField')}
                  >
                    <Copy01Icon size={14} />
                  </IconButton>
                </Tooltip>
              )}
              {isModified && (
                <Tooltip title={t('resetToOriginal')}>
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={() => handleResetField(field)}
                    aria-label={t('resetField')}
                  >
                    <Undo02Icon size={14} />
                  </IconButton>
                </Tooltip>
              )}
            </HStack>
          </HStack>

          {previewMode === 'split' || previewMode === 'edit' ? (
            <Textarea
              value={value}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              placeholder={placeholder}
              rows={previewMode === 'split' ? 6 : 12}
              resize="vertical"
              size="sm"
              fontFamily="mono"
            />
          ) : null}

          {(previewMode === 'split' || previewMode === 'preview') && value && (
            <Box
              mt={previewMode === 'split' ? 2 : 0}
              p={2}
              bg="bg.subtle"
              borderRadius="md"
              borderWidth="1px"
              borderColor="border.default"
              maxHeight="300px"
              overflowY="auto"
            >
              <MarkdownPreview source={value} compact />
            </Box>
          )}
        </Field.Root>
      );
    },
    [
      editedData,
      previewMode,
      validateField,
      isFieldModified,
      handleFieldChange,
      handleCopyField,
      handleResetField,
      t,
    ],
  );
  const content = (
    <VStack gap={3} padding={4} h="full" overflow="hidden">
      <HStack
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        flexShrink={0}
      >
        <VStack alignItems="flex-start" gap={1}>
          <HStack gap={2} alignItems="center">
            <FlashIcon size={18} color="var(--colors-orange-9)" />
            <Text fontSize="md" fontWeight="bold">
              {t('title')}
            </Text>
            {hasUnsavedChanges && (
              <Badge variant="emphasis" size="sm">
                {t('fieldsModified', { count: modifiedFieldsCount })}
              </Badge>
            )}
          </HStack>
          <Text fontSize="xs" color="fg.muted">
            {t('description')}
          </Text>
        </VStack>
        <HStack gap={1}>
          {renderMode === 'dialog' && (
            <Tooltip
              title={isFullscreen ? t('exitFullscreen') : t('fullscreen')}
            >
              <IconButton
                variant="ghost"
                size="xs"
                onClick={() => setIsFullscreen(!isFullscreen)}
                aria-label={t('toggleFullscreen')}
              >
                <Maximize02Icon size={14} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={t('close')}
            onClick={handleClose}
          >
            <Cancel01Icon size={14} />
          </IconButton>
        </HStack>
      </HStack>

      <HStack
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        px={2}
        py={1.5}
        bg="bg.subtle"
        borderRadius="md"
        flexWrap="wrap"
        gap={2}
        flexShrink={0}
      >
        <HStack gap={2} alignItems="center">
          <Badge
            size="sm"
            variant="fill"
            leadingContent={<FlashIcon size={10} />}
          >
            {t('neoAttackBadge')}
          </Badge>
          {!isDataValid && (
            <Badge
              size="sm"
              variant="emphasis"
              icon={<Alert01Icon size={10} />}
            >
              {t('missingRequiredFields')}
            </Badge>
          )}
        </HStack>

        <HStack gap={1}>
          <Tooltip title={t('editMode')}>
            <IconButton
              variant={previewMode === 'edit' ? 'solid' : 'ghost'}
              size="xs"
              onClick={() => setPreviewMode('edit')}
              aria-label={t('editMode')}
            >
              <PencilEdit02Icon size={14} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('splitView')}>
            <IconButton
              variant={previewMode === 'split' ? 'solid' : 'ghost'}
              size="xs"
              onClick={() => setPreviewMode('split')}
              aria-label={t('splitView')}
            >
              <ViewIcon size={14} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('previewMode')}>
            <IconButton
              variant={previewMode === 'preview' ? 'solid' : 'ghost'}
              size="xs"
              onClick={() => setPreviewMode('preview')}
              aria-label={t('previewMode')}
            >
              <File01Icon size={14} />
            </IconButton>
          </Tooltip>
          {hasUnsavedChanges && (
            <Tooltip title={t('resetAllFields')}>
              <Button variant="ghost" size="xs" onClick={handleResetAll}>
                <Undo02Icon size={14} />
                {t('resetAll')}
              </Button>
            </Tooltip>
          )}
        </HStack>
      </HStack>

      <Box flex={1} overflow="hidden" width="100%">
        <Tabs.Root
          defaultValue="config"
          variant="enclosed"
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
        >
          <Tabs.List flexShrink={0}>
            <Tabs.Trigger value="config">
              <HStack gap={2}>
                <Text fontSize="sm">{t('tabs.configuration')}</Text>
                {isFieldModified('signalId') && (
                  <CheckmarkCircle02Icon
                    size={12}
                    color="var(--colors-blue-9)"
                  />
                )}
              </HStack>
            </Tabs.Trigger>
            <Tabs.Trigger value="prompt">
              <HStack gap={2}>
                <Text fontSize="sm">{t('tabs.userAttackPrompt')}</Text>
                {isFieldModified('userAttackPrompt') && (
                  <CheckmarkCircle02Icon
                    size={12}
                    color="var(--colors-blue-9)"
                  />
                )}
              </HStack>
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="config" p={0} pt={3} flex={1} overflow="auto">
            <VStack gap={3} width="100%" paddingRight={2} alignItems="stretch">
              <VStack alignItems="start" gap={2} width="100%">
                <HStack gap={2} alignItems="center">
                  <Text fontSize="xs" fontWeight="medium">
                    {t('fields.signal')}
                  </Text>
                  <Text as="span" color="fg.error" fontSize="xs">
                    *
                  </Text>
                  {isFieldModified('signalId') && (
                    <Badge variant="emphasis" size="sm">
                      {t('modified')}
                    </Badge>
                  )}
                  {!editedData.signalId && (
                    <Tooltip title={t('validation.signalIdRequired')}>
                      <Alert01Icon size={14} color="var(--colors-fg-error)" />
                    </Tooltip>
                  )}
                </HStack>
                <SignalSelector
                  selectedSignalId={editedData.signalId}
                  onSignalSelect={handleSignalSelect}
                />
              </VStack>
            </VStack>
          </Tabs.Content>

          <Tabs.Content value="prompt" p={0} pt={3} flex={1} overflow="auto">
            <VStack gap={3} width="100%" paddingRight={2} alignItems="stretch">
              {renderLargeField({
                field: 'userAttackPrompt',
                label: t('fields.userAttackPrompt'),
                icon: <FlashIcon size={14} color="var(--colors-orange-9)" />,
                placeholder: t('placeholders.userAttackPrompt'),
                required: false,
              })}
            </VStack>
          </Tabs.Content>
        </Tabs.Root>
      </Box>

      <HStack
        gap={2}
        justifyContent="space-between"
        width="100%"
        pt={2}
        flexShrink={0}
        borderTopWidth="1px"
        borderColor="border.default"
      >
        <Text fontSize="xs" color="fg.muted">
          {hasUnsavedChanges
            ? t('fieldsModifiedCount', { count: modifiedFieldsCount })
            : t('noChanges')}
        </Text>
        <HStack gap={2}>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            {t('cancel')}
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={!isDataValid}>
            <FloppyDiskIcon size={14} />
            {t('triggerAttack')}
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );

  if (renderMode === 'inline') {
    return content;
  }

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => !open && handleClose()}
      lazyMount
      unmountOnExit
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content
          maxWidth={isFullscreen ? '95vw' : '900px'}
          width="90vw"
          height={isFullscreen ? '95vh' : '85vh'}
          maxHeight={isFullscreen ? '95vh' : '85vh'}
        >
          {content}
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
